import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// 'auto' follows the OS's prefers-color-scheme and keeps following it live
// (no stored override) — 'light'/'dark' are explicit, stored choices that
// override the OS setting until the user picks 'auto' again.
export type VwThemeMode = 'light' | 'dark' | 'auto';
export type VwThemeName = 'light' | 'dark';

const STORAGE_KEY = 'vw-theme';
const CONTRAST_KEY = 'vw-contrast';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private mediaQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  private modeSubject = new BehaviorSubject<VwThemeMode>(this.readStoredMode());
  mode$ = this.modeSubject.asObservable();
  private themeSubject = new BehaviorSubject<VwThemeName>(this.readAppliedTheme());
  theme$ = this.themeSubject.asObservable();
  // Orthogonal to light/dark/auto — a high-contrast viewer wants it paired
  // with whichever of those they already use, not a fourth theme option.
  private highContrastSubject = new BehaviorSubject<boolean>(this.readStoredContrast());
  highContrast$ = this.highContrastSubject.asObservable();

  constructor() {
    if (this.mediaQuery) {
      // Only matters while in 'auto' — if the user has an explicit
      // light/dark override stored, system changes are ignored until they
      // switch back to 'auto'.
      this.mediaQuery.addEventListener('change', () => {
        if (this.modeSubject.value === 'auto') {
          this.applyTheme(this.systemTheme());
        }
      });
    }
    this.applyContrast(this.highContrastSubject.value);
  }

  get current(): VwThemeName {
    return this.themeSubject.value;
  }

  get currentMode(): VwThemeMode {
    return this.modeSubject.value;
  }

  // The pre-boot inline script in index.html already set data-theme on
  // <html> before Angular loaded (to avoid a flash of the wrong theme) —
  // this just reads that back into the service's initial state.
  private readAppliedTheme(): VwThemeName {
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark' ? 'dark' : 'light';
  }

  private readStoredMode(): VwThemeMode {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'light' || stored === 'dark' ? stored : 'auto';
    } catch {
      return 'auto';
    }
  }

  private systemTheme(): VwThemeName {
    return this.mediaQuery && this.mediaQuery.matches ? 'dark' : 'light';
  }

  private applyTheme(theme: VwThemeName): void {
    document.documentElement.setAttribute('data-theme', theme);
    this.themeSubject.next(theme);
  }

  setMode(mode: VwThemeMode): void {
    this.modeSubject.next(mode);
    try {
      if (mode === 'auto') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, mode);
      }
    } catch {
      // localStorage unavailable (private mode, etc.) — still applies for this session
    }
    this.applyTheme(mode === 'auto' ? this.systemTheme() : mode);
  }

  // Kept for any existing caller still using the old binary API — now a
  // thin wrapper that picks an explicit mode rather than 'auto'.
  setTheme(theme: VwThemeName): void {
    this.setMode(theme);
  }

  // Three-way cycle: light -> dark -> auto -> light ...
  toggle(): void {
    const next: VwThemeMode = this.currentMode === 'light' ? 'dark' : this.currentMode === 'dark' ? 'auto' : 'light';
    this.setMode(next);
  }

  get isHighContrast(): boolean {
    return this.highContrastSubject.value;
  }

  private readStoredContrast(): boolean {
    try {
      return localStorage.getItem(CONTRAST_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private applyContrast(enabled: boolean): void {
    document.documentElement.setAttribute('data-contrast', enabled ? 'high' : 'normal');
  }

  toggleHighContrast(): void {
    const next = !this.highContrastSubject.value;
    this.highContrastSubject.next(next);
    try {
      localStorage.setItem(CONTRAST_KEY, String(next));
    } catch {
      // localStorage unavailable — still applies for this session
    }
    this.applyContrast(next);
  }
}
